import type { AxiosRequestConfig } from 'axios';
import { injectable } from 'inversify';
import type { AttachmentId, DocumentId, DocumentRevision, DocumentWithChildren } from '../types';
import { forEachTree, uuid } from '../utils';
import type { BaseDocumentsRepository } from './BaseDocuments.repository';
import { createBaseMapper } from './base-mapper';
import type { DocumentResource, SettingsResource } from './interfaces';
import type { BaseDocumentModel, DocumentAccess } from './models';
import type { CopyRequestArg, GetListOptions, MoveRequestArg } from './types';
import type { BaseDocumentVM } from './view-models';

@injectable()
export class BaseApiService<
  TEntity extends BaseDocumentVM = BaseDocumentVM,
  TModel extends BaseDocumentModel = BaseDocumentModel,
> implements DocumentResource<TEntity>
{
  protected mapper = createBaseMapper<TModel, TEntity>();

  protected documentsRepo: BaseDocumentsRepository<TModel>;

  protected settingsResource: SettingsResource;

  protected async loadSettings(id?: DocumentId, contentId?: DocumentId) {
    if (this.settingsResource && id && contentId) {
      await this.settingsResource.load(id, contentId);
    }
  }

  async getWorkspace(id: DocumentId, settingsContentId?: DocumentId, config?: AxiosRequestConfig) {
    const document = await this.get(id, config);

    await this.loadSettings(document.settingsId, settingsContentId ?? document.settingsContentId);

    return document;
  }

  async update(entity: TEntity, config?: AxiosRequestConfig) {
    const model = this.mapper.fromDomain(entity);
    const document = await this.documentsRepo.update(model, config);

    return this.mapper.toDomain(document);
  }

  async updateMany(entities: TEntity[], config?: AxiosRequestConfig) {
    const models = this.mapper.fromDomainArray(entities);
    const response = await this.documentsRepo.updateMany(models, config);

    return response.map(item => ({
      ok: item.ok,
      id: item.id,
      error: item.error,
      document: item.model ? this.mapper.toDomain(item.model) : undefined,
    }));
  }

  async copy(body: CopyRequestArg, config?: AxiosRequestConfig) {
    const document = await this.documentsRepo.copy(body, config);

    return this.mapper.toDomain(document);
  }

  async duplicateWorkspace(id: DocumentId) {
    const model = await this.documentsRepo.duplicateRoot(id);

    return this.mapper.toDomain(model);
  }

  async move(body: MoveRequestArg, config?: AxiosRequestConfig) {
    const documents = await this.documentsRepo.move(body, config);

    return this.mapper.toDomainArray(documents);
  }

  async getList(options?: GetListOptions) {
    const data = await this.documentsRepo.getList(options);

    if (Array.isArray(data)) {
      return this.mapper.toDomainArray(data);
    }

    return {
      ...data,
      items: this.mapper.toDomainArray(data.items),
    };
  }

  async createChildren(
    id: DocumentId,
    entities: DocumentWithChildren<TEntity>[],
    config?: AxiosRequestConfig,
  ) {
    const models = this.mapper.fromDomainTree(entities);

    const document = await this.documentsRepo.createChildren(id, models, config);

    return this.mapper.toDomain(document);
  }

  async getChildren(id: DocumentId) {
    const documents = await this.documentsRepo.getChildren(id);

    return this.mapper.toDomainArray(documents);
  }

  async removeChildren(...args: [DocumentId, DocumentId[], AxiosRequestConfig?]) {
    try {
      const document = await this.documentsRepo.removeChildren(...args);

      return {
        ok: true,
        data: this.mapper.toDomain(document),
      };
    } catch (error) {
      return {
        ok: false,
        error,
      };
    }
  }

  async getRoot(id?: DocumentId) {
    const idsPath = await this.getPathToRoot(id);
    const [rootId] = idsPath;
    const model = await this.get(rootId);

    const children = [];
    while (children.length < idsPath.length) {
      children.push(this.getChildren(idsPath[children.length]));
    }

    const documents = await Promise.all(children);

    return {
      documents: [model, ...documents.flat()],
      rootId,
    };
  }

  async createWorkspace(entity: TEntity) {
    const [model] = this.mapper.fromDomainTree([entity]);
    const document = await this.documentsRepo.createRoot(model);

    const root = await this.documentsRepo.createChildren(document._id, model.children || [], {
      headers: {
        'If-Match': `"${document._rev}"`,
      },
    });

    return [this.mapper.toDomain(root)];
  }

  remove(id: DocumentId, rev: DocumentRevision) {
    return this.documentsRepo.remove(id, rev);
  }

  async get(id: DocumentId, config?: AxiosRequestConfig) {
    const document = await this.documentsRepo.get(id, config);

    return this.mapper.toDomain(document);
  }

  async getTree(id: DocumentId, config?: AxiosRequestConfig) {
    const documents = await this.documentsRepo.getTree(id, config);

    return this.mapper.toDomainTree(documents);
  }

  import(
    id: DocumentId,
    area: DocumentId,
    entities: DocumentWithChildren<TEntity>[],
    config?: AxiosRequestConfig,
  ) {
    function iterate(item: DocumentWithChildren<TEntity>): DocumentWithChildren<TEntity> {
      return {
        ...item,
        area,
        _rev: undefined,
        _id: uuid(),
        children: item.children?.map((child: DocumentWithChildren<TEntity>) => iterate(child)),
      };
    }

    const clearEntities = entities.map(entity => iterate(entity));

    return this.createChildren(id, clearEntities, config);
  }

  getPathToRoot(id?: DocumentId | undefined) {
    return this.documentsRepo.getPathToRoot(id);
  }

  async expandTree(id: DocumentId): Promise<[TEntity[], DocumentId[]]> {
    const tree = await this.getTree(id);

    const documents: TEntity[] = [];
    const ids: DocumentId[] = [];

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    forEachTree(tree, ({ children, ...rest }) => {
      documents.push(rest as TEntity);
      ids.push(rest._id);
    });

    return [documents, ids];
  }

  async getParents(id: DocumentId) {
    const ids = await this.getPathToRoot(id);
    return this.getMany(ids);
  }

  async getMany(ids: DocumentId[]) {
    const models = await this.documentsRepo.getMany(ids);
    return this.mapper.toDomainArray(models);
  }

  bisect(docId: DocumentId) {
    return this.documentsRepo.bisect(docId);
  }

  async updateAccess(entity: TEntity, request: DocumentAccess[]) {
    const model = this.mapper.fromDomain(entity);
    const document = await this.documentsRepo.updateAccess(model, request);

    return this.mapper.toDomain(document);
  }

  getAccess(id: DocumentId) {
    return this.documentsRepo.getAccess(id);
  }

  getAttachmentList(docId: DocumentId) {
    return this.documentsRepo.getAttachmentList(docId);
  }

  setAttachment(docId: DocumentId, attachmentId: AttachmentId, _rev: DocumentRevision, file: File) {
    const id = attachmentId || uuid();
    return this.documentsRepo.setAttachment(docId, id, _rev, file);
  }

  getAttachment(docId: DocumentId, attachmentId: AttachmentId) {
    return this.documentsRepo.getAttachment(docId, attachmentId);
  }

  deleteAttachment(docId: DocumentId, attachmentId: AttachmentId, _rev: DocumentRevision) {
    return this.documentsRepo.deleteAttachment(docId, attachmentId, _rev);
  }
}
